import React, {useState, useCallback, useContext} from 'react';
import { View, Text } from 'react-native';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';

import Autocomplete from 'app/src/components/Autocomplete';
import DataState from 'app/src/components/DataState';
import AuthContext from 'app/src/contexts/AuthContext';
import Input from 'app/src/elements/inputs';
import { Button, ButtonGroup } from 'app/src/elements/buttons';

import { CREATE_LOCATION } from './queries';
import { ButtonContainer } from './elements';

export const MULTI_EMAIL = /^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,}(\s*,?\s*)*)+$/
export const MULTI_EMAIL_MESSAGE =
  'Email address must be a valid address, ' +
  'multiple emails can be separated by commas';

const NewLocation = ({setShowModal, refetch, locationName, saleAssetId}) => {
  const { settings } = useContext(AuthContext);

  const [submitting, setSubmitting] = useState();
  const [createLocation] = useMutation(CREATE_LOCATION, {
    onCompleted: () => {
      if(refetch) {
        refetch();
      }
      setShowModal(false);
    }
  });

  const onSubmit = useCallback(input => {
    setSubmitting(true);
    createLocation({ variables: input });
  });

  const { control, handleSubmit, setValue, register, formState: { errors } } = useForm({
    defaultValues: {
      stateId: '',
      street: '',
      city: '',
      state: '',
      postalCode: '',
      phoneNumber: '',
      contact: '',
      releaseYardContact: '',
      storageFee: '',
      storageFeeText: '',
      runningUnitsOnly: false,
      equiplincContactId: null,
      releaseYardEmail: ''
    }
  });

  if (submitting) { return <DataState.Loading /> }

  return (
    <View style={{flexDirection: 'row', gap: 20}}>
      <View style={{flex: 1}}>
        <Input.Text
          label="State ID:"
          name="stateId"
          control={control}
          errors={errors}
        />
        <Input.Text
          label="Name:"
          name="name"
          control={control}
          errors={errors}
        />
        <Input.Text
          label="Street:"
          name="street"
          hint="Address displays on release."
          control={control}
          errors={errors}
        />
        <Input.Text
          label="City:"
          name="city"
          control={control}
          errors={errors}
        />
        <Input.StatePicker
          label="State:"
          name="state"
          control={control}
          errors={errors}
        />
        <Input.Text
          label="Zip:"
          name="postalCode"
          control={control}
          errors={errors}
        />
        <Input.Phone
          label="Phone Number:"
          name="phoneNumber"
          control={control}
          errors={errors}
        />
        <Input.Checkbox
          label="Running Units Only:"
          name="runningUnitsOnly"
          control={control}
          errors={errors}
        />
        <Input.Text
          label="Storage Fee Notes:"
          name="storageFee"
          control={control}
          errors={errors}
        />
      </View>
      <View style={{flex: 1}}>
        <Input.Text
          label="Yard POC"
          name="releaseYardContact"
          hint="Displays on release. List name, email, phone # as desired."
          control={control}
          errors={errors}
        />
        <Input.Text
          label="Yard Email:"
          name="releaseYardEmail"
          hint="Release will be sent to this address, but will not display on release."
          control={control}
          errors={errors}
          id="releaseYardEmail"
          {...register("releaseYardEmail", {
            validate: {
              maxLength: v => (
                v.length <= 250 ||
                  'The email should have at most 250 characters'
              ),
              matchPattern: v => (
                !v || MULTI_EMAIL.test(v) || MULTI_EMAIL_MESSAGE
              ),
            }
          })}
        />
        <Input.TextArea
          label="Custom Storage Fee Text for Release:"
          name="storageFeeText"
          control={control}
          errors={errors}
        />
        <Text>If left blank, default text will be used:</Text>
        <Text style={{width: 300}}>{settings?.storageFeeText}</Text>
        <View style={{height: 10}}></View>
        <Autocomplete
          label="ELAG POC"
          hint="Displays on release with email and phone #."
          placeHolder=""
          resource="User"
          setValue={(name, value) => setValue('equiplincContactId', value)}
          name="equiplincContactId"
        />
        <ButtonContainer>
          <ButtonGroup>
            <Button label="Create Location" onPress={handleSubmit(onSubmit)} />
          </ButtonGroup>
        </ButtonContainer>
      </View>
    </View>
  );
};

export default NewLocation;
